import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { switchAccount } from 'redux/features/auth/authSlice'
import { isFailed, isPending, isSuccess } from 'redux/toolkit/api'
import { setErrorSnackBar } from 'redux/features/app/appSlice'
import { useFormatMessage } from 'lib/localization'
import { ENTRY_PATHS } from 'components/libs/routes/useAppTypeEntryPaths'

interface Account {
  id: string
  name: string
}

export interface State {
  accounts: Account[]
  accountSwitchingInProgress: boolean
  activeAccountId?: string
  selectedAccount?: Account
}

export interface EventHandlers {
  onSelectAccountCallback: (newSelectedAccount: Account) => void
}

const BASE_I18N_KEY = 'ess.account_switcher'

export const useAccountSwitcherLogic = (): [State, EventHandlers] => {
  const dispatch = useAppDispatch()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const {
    accounts,
    selectedBCCAccountId,
    accountSwitchingInProgress,
    accountSwitchIsSuccess,
    accountSwitchIsFailed,
    switchAccountResponse,
    defaultPortalId,
    appType,
    authMode
  } = useAppSelector(_store => ({
    accounts: _store.auth.accessTokenObject?.userInfo?.accounts,
    selectedBCCAccountId: _store.auth.accessTokenObject?.bccAccountId,
    accountSwitchingInProgress: isPending(_store.auth.api.switchAccountApiStatus),
    accountSwitchIsSuccess: isSuccess(_store.auth.api.switchAccountApiStatus),
    accountSwitchIsFailed: isFailed(_store.auth.api.switchAccountApiStatus),
    switchAccountResponse: _store.auth.switchAccount,
    defaultPortalId: _store.auth.accessTokenObject?.userInfo?.defaultPortalId,
    appType: _store.app.appType,
    authMode: _store.auth.accessTokenObject?.authMode
  }))
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>()
  const [isRedirectingToOAuth, setIsRedirectingToOAuth] = useState(false)

  // Map accounts to append (default) to name if id matches defaultPortalId
  const mappedAccounts = useMemo(() => {
    if (!accounts) return []
    return accounts.map(acc =>
      acc.id === defaultPortalId
        ? {
            ...acc,
            name: `${acc.name} ${formatMessage('default_account_suffix')}`.trim()
          }
        : acc
    )
  }, [accounts, defaultPortalId, formatMessage])

  useEffect(() => {
    if (selectedAccount) {
      // Login App sessions must use OAuth2 flow for account switching.
      // SOAP /switch-account endpoint is not available for Login App sessions.
      // Redirect to React OAuth2 authorize page which handles auth process creation.
      if (authMode === 'login_app') {
        setIsRedirectingToOAuth(true)
        window.location.href = `/admin/oauth2/authorize?connection=login_app&account_hint=${selectedAccount.id}`
        return
      }

      // C1: DEAD CODE - SOAP switch-account fallback
      // Only reached when authMode is undefined (SOAP session) or not 'login_app'
      // Safe to delete when SOAP sessions are fully deprecated
      dispatch(switchAccount(selectedAccount.id))
    }
  }, [selectedAccount, dispatch, authMode])

  // account switch is success
  useEffect(() => {
    if (accountSwitchIsSuccess && selectedAccount && !!switchAccountResponse?.success) {
      if (switchAccountResponse.newRegionUrl) {
        // Switch to the url provided for the selected account
        window.location.href = switchAccountResponse.newRegionUrl
      } else {
        // Redirect to app entry point to prevent stale route params (e.g., domainId) from persisting
        const entryPath = ENTRY_PATHS[appType]
        window.location.href = entryPath.url()
      }
    }
  }, [appType, accountSwitchIsSuccess, selectedAccount, switchAccountResponse])

  // account switch is failed
  useEffect(() => {
    if (selectedAccount && (accountSwitchIsFailed || (accountSwitchIsSuccess && !switchAccountResponse?.success))) {
      dispatch(setErrorSnackBar({ message: 'account_switch_failed', params: [selectedAccount.name] }))
      setSelectedAccount(undefined)
    }
  }, [dispatch, accountSwitchIsFailed, accountSwitchIsSuccess, selectedAccount, switchAccountResponse])

  const onSelectAccountCallback = useCallback(
    (newSelectedAccount: Account) => {
      const newAccount = mappedAccounts?.find(account => account.id === newSelectedAccount.id)

      if (newAccount && selectedAccount?.id !== newAccount.id) {
        setSelectedAccount(newAccount)
      }
    },
    [mappedAccounts, selectedAccount]
  )

  return useMemo(
    () => [
      {
        accounts: mappedAccounts,
        accountSwitchingInProgress: accountSwitchingInProgress || accountSwitchIsSuccess || isRedirectingToOAuth,
        activeAccountId: selectedBCCAccountId,
        selectedAccount
      },
      {
        onSelectAccountCallback
      }
    ],
    [
      mappedAccounts,
      onSelectAccountCallback,
      accountSwitchingInProgress,
      accountSwitchIsSuccess,
      isRedirectingToOAuth,
      selectedBCCAccountId,
      selectedAccount
    ]
  )
}
